import { useState, useEffect } from "react";
import { analyticsService } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

const COLORS = ["#3B82F6", "#16A34A", "#F59E0B", "#DC2626", "#8B5CF6", "#EC4899"];

const EmptyState = ({ title, message }) => (
  <div className="h-80 flex flex-col items-center justify-center text-gray-400">
    <AlertCircle className="w-12 h-12 mb-3" />
    <p className="font-medium text-gray-600">{title}</p>
    <p className="text-sm">{message}</p>
  </div>
);

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await analyticsService.getDashboardStats();
        setStats(data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const departmentData = stats ? Object.entries(stats.departments).map(([name, value]) => ({
    name,
    members: value
  })) : [];

  const taskStatusData = stats ? [
    { name: "Pending", value: stats.taskStats.pending },
    { name: "In Progress", value: stats.taskStats.inProgress },
    { name: "Completed", value: stats.taskStats.completed }
  ] : [];

  const monthlyData = stats?.monthlyTrends;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-action-blue" />
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Analytics</h1>
        <p className="text-gray-500">View performance metrics and insights</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 mb-1">Total Team Members</p>
            <p className="text-3xl font-bold text-navy">{stats?.totalUsers || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 mb-1">Total Tasks</p>
            <p className="text-3xl font-bold text-navy">{stats?.totalTasks || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 mb-1">Active Projects</p>
            <p className="text-3xl font-bold text-navy">{stats?.activeProjects || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 mb-1">Departments</p>
            <p className="text-3xl font-bold text-navy">{stats?.departmentCount || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Task Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="tasks" stroke="#3B82F6" strokeWidth={2} name="Created" />
                    <Line type="monotone" dataKey="completed" stroke="#16A34A" strokeWidth={2} name="Completed" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState 
                title="Not enough data yet" 
                message="Create tasks to see trends over time" 
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Task Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {taskStatusData.some(d => d.value > 0) ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {taskStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState 
                title="No tasks yet" 
                message="Create tasks to see status distribution" 
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Members by Department</CardTitle>
          </CardHeader>
          <CardContent>
            {departmentData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={departmentData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" fontSize={12} />
                    <YAxis dataKey="name" type="category" fontSize={12} width={120} />
                    <Tooltip />
                    <Bar dataKey="members" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState 
                title="No team members yet" 
                message="Team members will appear here by department" 
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;